import React from 'react';
import PageLayout from '../components/PageLayout';
import { useAppSettings } from '../lib/i18n';
import { Calculator, Wallet, PieChart, Target, Shield, Percent, Coins, BookOpen } from 'lucide-react';

const ABBREVIATIONS = [
  { k: 'EMI', v: 'Equated Monthly Installment — the fixed monthly amount you pay to your lender.' },
  { k: 'ROI', v: 'Rate of Interest — the annual interest rate quoted by the lender (in %).' },
  { k: 'P', v: 'Principal — the original loan amount you borrow.' },
  { k: 'P_eff', v: 'Effective Principal — the principal used to compute EMIs after any credit-days or subvention discounts.' },
  { k: 'R', v: 'Monthly Interest Rate — annual ROI divided by 12, expressed as a decimal (e.g. 8.4% p.a. → R = 0.007).' },
  { k: 'N', v: 'Number of Installments — total EMIs across the loan tenure (tenure in years × 12).' },
  { k: 'p.a.', v: 'Per Annum — the interest rate applies over one year.' },
  { k: 'CI', v: 'Credit-days Interest — interest amount absorbed by the seller during the grace period, deducted from P.' },
  { k: 'd', v: 'Discounting Rate — the annualised rate used to compute the credit-days interest (defaults to the loan ROI).' },
  { k: 'Subvention', v: 'A one-time upfront discount offered by the builder / dealer / manufacturer that reduces the effective principal.' },
  { k: 'Reducing balance', v: 'Interest is recomputed every month on the outstanding balance — the standard method used by most banks.' },
  { k: 'Flat rate', v: 'Interest is charged on the original principal for the whole tenure — usually more expensive than reducing balance.' },
  { k: 'Moratorium', v: 'A pause in EMI payments (typically at the start). Interest still accrues and is either capitalised or paid separately.' },
  { k: 'Prepayment', v: 'A payment made in addition to the regular EMI that directly reduces the outstanding principal.' },
];

export default function AboutPage() {
  const { t } = useAppSettings();

  const features = [
    { id: 'instant', icon: Calculator, t: t('feature_1_t'), d: t('feature_1_d') },
    { id: 'breakup', icon: PieChart, t: t('feature_2_t'), d: t('feature_2_d') },
    { id: 'budget', icon: Wallet, t: t('feature_3_t'), d: t('feature_3_d') },
  ];

  return (
    <PageLayout>
      <section className="emi-wrap pt-8 pb-16">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#032D60] tracking-tight">About EMIWise</h1>
          <p className="mt-3 text-base text-slate-600 dark:text-slate-400 max-w-3xl leading-relaxed">
            EMIWise is a modern, no-nonsense loan planning tool built to help you understand exactly what a loan will cost — before you sign for one.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
            <h2 className="text-xl font-semibold text-[#032D60]">{t('what_is_emi_title')}</h2>
            <p className="mt-3 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">{t('what_is_emi_body')}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              {features.map((f) => (
                <div key={f.id} className="rounded-xl border border-slate-200 p-4 hover:border-[#B7E1FA] transition-colors">
                  <div className="h-9 w-9 rounded-lg bg-[#EAF5FE] flex items-center justify-center text-[#0176D3]">
                    <f.icon className="h-4 w-4" />
                  </div>
                  <div className="mt-3 text-sm font-semibold text-[#032D60] dark:text-slate-100">{f.t}</div>
                  <div className="mt-1 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">{f.d}</div>
                </div>
              ))}
            </div>

            {/* Advanced options — Credit Days + Subvention only */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div className="rounded-xl border border-[#B7E1FA] bg-gradient-to-b from-white to-[#EAF5FE] dark:from-[#0F1B36] dark:to-[#1E3A5F] p-4">
                <div className="h-9 w-9 rounded-lg bg-white dark:bg-[#0F1B36] flex items-center justify-center text-[#0176D3] border border-[#B7E1FA] dark:border-[#1E4A72]">
                  <Percent className="h-4 w-4" />
                </div>
                <div className="mt-3 text-sm font-semibold text-[#032D60] dark:text-slate-100">Credit Days</div>
                <div className="mt-1 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                  A grace period offered by the builder / dealer / manufacturer. The interest for those days is discounted from the effective principal — reducing your EMI and effective ROI.
                </div>
              </div>
              <div className="rounded-xl border border-[#A7F3D0] bg-gradient-to-b from-white to-[#ECFDF5] dark:from-[#0F1B36] dark:to-[#0B2A1D] p-4">
                <div className="h-9 w-9 rounded-lg bg-white dark:bg-[#0F1B36] flex items-center justify-center text-[#059669] border border-[#A7F3D0] dark:border-[#0F5D42]">
                  <Coins className="h-4 w-4" />
                </div>
                <div className="mt-3 text-sm font-semibold text-[#032D60] dark:text-slate-100">Interest Subvention</div>
                <div className="mt-1 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                  A one-time discount that is deducted from the effective principal before EMIs are computed. Can be combined with credit days to stack the benefit.
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-xl bg-[#EAF5FE] border border-[#B7E1FA] p-5">
              <h3 className="text-sm font-semibold text-[#032D60]">{t('formula_title')}</h3>
              <p className="mt-2 text-sm text-slate-700">
                <span className="font-mono bg-white px-2 py-0.5 rounded border border-[#B7E1FA] text-[#0176D3]">
                  EMI = [P × R × (1+R)^N] / [(1+R)^N − 1]
                </span>
              </p>
              <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">{t('formula_note')}</p>
            </div>

            {/* Advanced formulas */}
            <div className="mt-6 rounded-xl border border-[#C7D2FE] bg-gradient-to-br from-[#EEF2FF] to-[#E0E7FF] dark:from-[#1B1240] dark:to-[#251A5A] p-5">
              <h3 className="text-sm font-bold text-[#4338CA] dark:text-indigo-300 uppercase tracking-wide">Advanced calculation formulas</h3>
              <div className="mt-3 space-y-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                <div>
                  <div className="font-semibold text-[#032D60] dark:text-slate-100">Credit-days discount</div>
                  <p className="mt-1"><span className="font-mono bg-white dark:bg-[#0F1B36] px-2 py-0.5 rounded border border-[#C7D2FE] text-[#4338CA] dark:text-indigo-300">CI = P × (d / 100) × (days / 365)</span></p>
                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">where <b>d</b> is the discounting rate (defaults to the loan ROI) and <b>days</b> is the grace period offered by the seller. CI is <b>subtracted</b> from the principal before EMIs are computed.</p>
                </div>
                <div>
                  <div className="font-semibold text-[#032D60] dark:text-slate-100">Subvention adjustment</div>
                  <p className="mt-1"><span className="font-mono bg-white dark:bg-[#0F1B36] px-2 py-0.5 rounded border border-[#C7D2FE] text-[#4338CA] dark:text-indigo-300">P_eff = P − CI − Subvention</span></p>
                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">EMIs are calculated on the effective principal P_eff. Either or both discounts can apply — their effect stacks.</p>
                </div>
                <div>
                  <div className="font-semibold text-[#032D60] dark:text-slate-100">Effective ROI</div>
                  <p className="mt-1"><span className="font-mono bg-white dark:bg-[#0F1B36] px-2 py-0.5 rounded border border-[#C7D2FE] text-[#4338CA] dark:text-indigo-300">solve r: EMI = [P × r × (1+r)^N] / [(1+r)^N − 1]</span></p>
                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">The calculator inverts the EMI formula (numerically, on the <i>original</i> principal) to show the equivalent annual rate the borrower is really paying after discounts.</p>
                </div>
              </div>
            </div>

            {/* Glossary / abbreviations */}
            <div className="mt-6 rounded-xl border border-slate-200 bg-white dark:bg-[#101B33] p-5" data-testid="about-abbreviations">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-8 w-8 rounded-lg bg-[#EAF5FE] text-[#0176D3] flex items-center justify-center">
                  <BookOpen className="h-4 w-4" />
                </div>
                <h3 className="text-sm font-bold text-[#032D60] dark:text-slate-100 uppercase tracking-wide">Formula abbreviations & glossary</h3>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {ABBREVIATIONS.map((a) => (
                  <div key={a.k} className="flex items-start gap-2 border-b border-slate-100 dark:border-[#1E2A44] py-2">
                    <dt className="font-mono font-bold text-[#0176D3] dark:text-sky-300 min-w-[80px] shrink-0">{a.k}</dt>
                    <dd className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">{a.v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="h-10 w-10 rounded-lg bg-[#ECFDF5] text-[#059669] flex items-center justify-center">
                <Target className="h-5 w-5" />
              </div>
              <h3 className="mt-3 text-base font-semibold text-[#032D60]">Our mission</h3>
              <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                Make borrowing decisions transparent for everyone — whether it’s your first personal loan or a multi-crore home loan.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="h-10 w-10 rounded-lg bg-[#FDF2F8] text-[#DB2777] flex items-center justify-center">
                <Shield className="h-5 w-5" />
              </div>
              <h3 className="mt-3 text-base font-semibold text-[#032D60]">Privacy first</h3>
              <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                All calculations run in your browser. We do not collect, store or share your loan details.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </PageLayout>
  );
}
